import { useSelector } from 'react-redux'
import { ActiveClaimCheckStatus } from '@typesApp/ClaimRecord'
import { AuthToken, Customer, CustomerContext } from '@typesApp/Customer'
import { activeClaimCheckStatusSelector, eligibleProductCheckInProgressSelector } from '@redux/slices/claimStatusSlice'
import {
  authTokenErrorSelector,
  authTokenReadySelector,
  authTokenSelector,
  connectorOrderCompleteSelector,
  connectorCustomerSelector,
  connectorCustomerContextSelector,
  isSessionInitializingSelector,
  loggingOutSelector,
  sessionExpiredSelector,
  claimRecordUpdateInProgressSelector,
  claimRecordUpdatePollingInProgressSelector,
} from '@redux/slices/connectorSlice'

type UseSessionInfoReturn = {
  isAnonymousUser?: boolean
  isGuestUser?: boolean
  customer?: Customer
  customerContext?: CustomerContext
  authToken?: AuthToken
  isTokenError?: boolean
  isTokenReady?: boolean
  isLoggingOut?: boolean
  isAuthenticated?: boolean
  isSessionReady?: boolean
  isSessionExpired?: boolean
  isOrderComplete?: boolean
  isActiveDataChecked?: boolean
  isSessionInitializing?: boolean
  isClaimRecordUpdateInProgress?: boolean
  isClaimRecordUpdatePollingInProgress?: boolean
  isEligibleProductCheckInProgress?: boolean
}

export const useSessionInfo = (): UseSessionInfoReturn => {
  const authToken = useSelector(authTokenSelector)
  const customer = useSelector(connectorCustomerSelector)
  const isTokenError = useSelector(authTokenErrorSelector)
  const isTokenReady = useSelector(authTokenReadySelector)
  const isLoggingOut = useSelector(loggingOutSelector)
  const isSessionExpired = useSelector(sessionExpiredSelector)
  const isOrderComplete = useSelector(connectorOrderCompleteSelector)
  const activeDataStatus = useSelector(activeClaimCheckStatusSelector)
  const isSessionInitializing = useSelector(isSessionInitializingSelector)
  const isClaimRecordUpdateInProgress = useSelector(claimRecordUpdateInProgressSelector)
  const isClaimRecordUpdatePollingInProgress = useSelector(claimRecordUpdatePollingInProgressSelector)
  const isEligibleProductCheckInProgress = useSelector(eligibleProductCheckInProgressSelector)
  const customerContext = useSelector(connectorCustomerContextSelector)

  const isSessionReady = isTokenReady && Boolean(customer)
  const isGuestUser = customerContext?.customerType === 'GUEST'
  const isAnonymousUser = customerContext?.customerType === 'ANONYMOUS'
  const isAuthenticated = customerContext?.customerType === 'REGISTERED'

  return {
    isAnonymousUser,
    isGuestUser,
    customer,
    customerContext,
    authToken,
    isTokenError,
    isTokenReady,
    isLoggingOut,
    isSessionReady,
    isSessionExpired,
    isOrderComplete,
    isAuthenticated,
    isActiveDataChecked: activeDataStatus === ActiveClaimCheckStatus.COMPLETE,
    isSessionInitializing,
    isClaimRecordUpdateInProgress,
    isClaimRecordUpdatePollingInProgress,
    isEligibleProductCheckInProgress,
  }
}
